var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { endOfMonth, format } from 'date-fns';
import { useFormik } from 'formik';
import InvoicesTable from '../../organisms/InvoicesTable';
import Button from '../../atoms/BaseButton';
import { invoice as invoiceApi } from '../../../api';
import { useRequest } from '../../../hooks/requestHook';
import { useNotify } from '../../../hooks/notificationHook';
import { camelize } from '../../../utils/camelize';
import FormikDatePicker from '../../atoms/FormikDatePicker';
import { FormikTextField } from '../../atoms/TextField';
import PageSubmitButton from '../../molecules/PageSubmitButton';
import FullScreenDialog from '../../molecules/FullScreenDialog';
import { AccordionExpandIcon } from '../../atoms/Icons';
var REQUIRED_FIELDS = ['created_date', 'expired_date'];
var InvoicesContent = function (_a) {
    var order = _a.order, onReloadOrder = _a.onReloadOrder;
    var t = useTranslation().t;
    var notify = useNotify();
    var _b = React.useState(false), loading = _b[0], setLoading = _b[1];
    var _c = React.useState(false), dialogOpen = _c[0], setDialogOpen = _c[1];
    var createRequest = useRequest(invoiceApi.create);
    var invoiceCreatable = React.useMemo(function () {
        return (order.paymentTiming !== 'after_each_delivery' &&
            order.invoices.length === 0);
    }, [order]);
    var onSubmit = React.useCallback(function (values) { return __awaiter(void 0, void 0, void 0, function () {
        var response;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setLoading(true);
                    return [4 /*yield*/, createRequest({
                            order_id: order.id,
                            invoice: values,
                        })];
                case 1:
                    response = _a.sent();
                    setLoading(false);
                    if (!response)
                        return [2 /*return*/, false];
                    onReloadOrder();
                    notify('success', [t('messages.createSuccess')]);
                    setDialogOpen(false);
                    return [2 /*return*/];
            }
        });
    }); }, [t, order.id, notify, createRequest, onReloadOrder]);
    var validate = React.useCallback(function (values) {
        var errors = {};
        REQUIRED_FIELDS.forEach(function (field) {
            if (!values[field] || values[field] === '') {
                errors[field] = t('errors.isRequired', {
                    field: t("params.order.".concat(camelize(field))),
                });
            }
        });
        return errors;
    }, [t]);
    var formik = useFormik({
        initialValues: {
            created_date: format(new Date(), 'yyyy-MM-dd'),
            expired_date: format(endOfMonth(new Date()), 'yyyy-MM-dd'),
            note: '',
        },
        onSubmit: onSubmit,
        validateOnChange: true,
        validateOnBlur: true,
        validate: validate,
    });
    return (React.createElement(Box, null,
        React.createElement(Box, { textAlign: "right" },
            React.createElement(Button, { onClick: function () { return setDialogOpen(true); }, disabled: !invoiceCreatable },
                React.createElement(AccordionExpandIcon, null),
                React.createElement(Typography, { sx: {
                        marginLeft: '5px',
                    } }, t('pages.offerList.createInvoices')))),
        React.createElement(InvoicesTable, { role: "admin", deletable: true, invoices: order.invoices, onReload: onReloadOrder }),
        React.createElement(FullScreenDialog, { open: dialogOpen, onClose: function () { return setDialogOpen(false); } },
            React.createElement("form", { onSubmit: formik.handleSubmit },
                React.createElement(FormikDatePicker, { required: true, fullWidth: true, margin: "dense", label: t('params.invoice.createdDate'), name: "created_date", formik: formik }),
                React.createElement(FormikDatePicker, { required: true, fullWidth: true, margin: "dense", label: t('params.invoice.expiredDate'), name: "expired_date", formik: formik }),
                React.createElement(FormikTextField, { fullWidth: true, margin: "dense", label: t('params.invoice.note'), name: "note", rows: 3, multiline: true, formik: formik }),
                React.createElement(PageSubmitButton, { disabled: !formik.isValid || loading }, t('buttons.create'))))));
};
export default InvoicesContent;
